/**
 * How a client-account code travels (backend messenger README §4.7). Boona
 * pays for the codes, so Telegram goes first — about a cent a code — and SMS
 * is the fallback where the salon has it (owner, 2026-09-29). Since
 * 2026-10-27 the SMS step may arrive by WhatsApp or Viber, where Prelude's
 * dashboard finds a messenger cheaper than SMS: the status names the app.
 */

export type CodeChannel = "telegram" | "sms";

/** The app the code went to (POST /public/client/otp/status `via`). */
export type CodeVia = "telegram" | "sms" | "whatsapp" | "viber";

/** What the backend says about the latest code (POST /public/client/otp/status). */
export type CodeStatus =
    | "pending" | "sent" | "no_telegram" | "send_failed" | "verified" | "expired" | "none";

const KNOWN: CodeChannel[] = ["telegram", "sms"];
const APPS: CodeVia[] = ["telegram", "sms", "whatsapp", "viber"];

/**
 * The salon's channels in the order to offer them. A backend from before the
 * choice sends no list: its codes go by SMS, so the screen says SMS.
 */
export function codeChannels(fromSettings: unknown): CodeChannel[] {
    if (!Array.isArray(fromSettings)) return ["sms"];
    const list = KNOWN.filter((ch) => fromSettings.includes(ch));
    return list.length ? list : ["sms"];
}

/** The channel a code screen opens with: the first one offered. */
export function firstChannel(channels: CodeChannel[]): CodeChannel {
    return channels[0] ?? "sms";
}

/** The other way to send, if the salon has one — the "by SMS instead" link. */
export function fallbackChannel(channels: CodeChannel[], current: CodeChannel): CodeChannel | null {
    return channels.find((ch) => ch !== current) ?? null;
}

/**
 * Every code is sent by the central worker a moment after the request; until
 * it answers (and says which app the code went to), the screen keeps asking.
 */
export function stillWaiting(status: CodeStatus): boolean {
    return status === "pending";
}

/**
 * The app to name on the code screen. A backend from before `via` (or one
 * naming an app the widget does not know) is the step's own channel.
 */
export function viaOf(value: unknown, channel: CodeChannel): CodeVia {
    return APPS.includes(value as CodeVia) ? (value as CodeVia) : channel;
}

/** The worker could not deliver the code: offer the fallback. */
export function undelivered(status: CodeStatus): boolean {
    return status === "no_telegram" || status === "send_failed";
}

/** How long the screen keeps asking before it stops (the worker answers in seconds). */
export const STATUS_POLL_MS = 2000;
export const STATUS_POLL_LIMIT = 30;
